import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import { venueDetailsMapping } from '../misc/venueDetailsMapping';
import 'leaflet/dist/leaflet.css';

import '../styles/Distance.css';
import '../styles/buttons.css';

import { pinkIcon, blueIcon } from "../components/MapIcons";

import DangerIcon from '../assets/modal-icon-danger.png';

const haversineDistance = (coords1, coords2) => {
  const toRad = x => (x * Math.PI) / 180;
  const lat1 = coords1.latitude;
  const lon1 = coords1.longitude;
  const lat2 = coords2.latitude;
  const lon2 = coords2.longitude;
  const R = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const Distance = ({
  user_id,
  accountStatus,
  selectedVenues,
  setSelectedVenues,
  tagFilteredVenues,
}) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedDistance, setSelectedDistance] = useState('');
  const [customLocationInput, setCustomLocationInput] = useState('');
  const [mapHeight, setMapHeight] = useState('700px');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setMapHeight(
        window.innerWidth < 768 ? '500px' : '700px'
      );
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () =>
      window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(position => {
      setCurrentLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
    });
  }, []);

  const getMaxAllowedVenues = () => {
    const plan = accountStatus?.subscription.plan;
    return plan === 'trial' || plan === 'essentials' ? 1 : Infinity;
  };

  const handleMarkerClick = venueName => {
    if (!accountStatus) {
      alert('Loading account status, please wait.');
      return;
    }
    const maxVenues = getMaxAllowedVenues();
    if (selectedVenues.includes(venueName)) {
      setSelectedVenues(
        selectedVenues.filter(name => name !== venueName)
      );
      return;
    }
    if (selectedVenues.length >= maxVenues) {
      setModalMessage(
        'Due to your current plan, you can only select one venue. Upgrade to view more.'
      );
      setShowModal(true);
      return;
    }
    setSelectedVenues([...selectedVenues, venueName]);
  };

  let allVenues = [];
  if (tagFilteredVenues?.length) {
    allVenues = tagFilteredVenues.map(v => {
      const details = venueDetailsMapping[v.name];
      return {
        name: v.name,
        latitude: details?.latitude,
        longitude: details?.longitude,
        title: details?.title,
        caption: details?.caption,
        imageUrl: details?.imageUrl,
      };
    });
  }

  const filteredVenues =
    selectedDistance && currentLocation
      ? allVenues.filter(venue => {
          if (!venue.latitude || !venue.longitude)
            return false;
          const distance = haversineDistance(currentLocation, {
            latitude: venue.latitude,
            longitude: venue.longitude,
          });
          return distance <= Number(selectedDistance);
        })
      : allVenues;

  const mapCenter = currentLocation
    ? [currentLocation.latitude, currentLocation.longitude]
    : filteredVenues.length
    ? [filteredVenues[0].latitude, filteredVenues[0].longitude]
    : [0, 0];

  const handleCustomLocationSubmit = async e => {
    e.preventDefault();
    if (!customLocationInput) return;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          customLocationInput
        )}`
      );
      const data = await response.json();
      if (data.length) {
        const { lat, lon } = data[0];
        setCurrentLocation({
          latitude: parseFloat(lat),
          longitude: parseFloat(lon),
        });
      } else {
        alert('Location not found.');
      }
    } catch (err) {
      console.error(err);
      alert('Error fetching location.');
    }
  };

  return (
    <div className="distance-page-container" id="dist-root">
      <div className="logo-spacer" />

      <div className="page-instructions">
        <h1>Discover Venues by Distance</h1>
        <p>
          Filter venues matching your tags, optionally limit by distance,
          then click a marker to select.
        </p>
        <p className="pin-explanation">
          <strong>Red:</strong> previously selected venue &nbsp;|&nbsp;{' '}
          <strong>Black:</strong> other matching venue
        </p>
      </div>

      <div className="distance-controls" id="dist-controls">
        <label htmlFor="distance-select">
          Select a distance (miles) from your current location:
        </label>
        <select
          id="distance-select"
          value={selectedDistance}
          onChange={e => setSelectedDistance(e.target.value)}
        >
          <option value="">All</option>
          <option value="5">5 miles</option>
          <option value="10">10 miles</option>
          <option value="20">20 miles</option>
          <option value="50">50 miles</option>
        </select>
      </div>

      <div className="custom-location-control" id="dist-custom">
        <form onSubmit={handleCustomLocationSubmit}>
          <label htmlFor="custom-location">
            Or enter a location:
          </label>
          <input
            type="text"
            id="custom-location"
            value={customLocationInput}
            onChange={e => setCustomLocationInput(e.target.value)}
            placeholder="City, address, etc."
          />
          <button
            type="submit"
            className="btn btn-primary"
          >
            Set Location
          </button>
        </form>
      </div>

      <div className="map-container" id="dist-map" style={{ position: 'relative' }}>
        <MapContainer
          center={mapCenter}
          zoom={12}
          style={{ height: mapHeight, width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {filteredVenues.map(venue => (
            <Marker
              key={venue.name}
              position={[venue.latitude, venue.longitude]}
              icon={
                selectedVenues.includes(venue.name)
                  ? pinkIcon
                  : blueIcon
              }
              eventHandlers={{
                click: () => handleMarkerClick(venue.name),
              }}
            >
              <Tooltip direction="top" offset={[0, -20]} opacity={1}>
                <div className="tooltip-content">
                  {venue.imageUrl && (
                    <img
                      src={venue.imageUrl}
                      alt={venue.title}
                      className="tooltip-image"
                    />
                  )}
                  <strong>{venue.title}</strong>
                  <p>{venue.caption}</p>
                </div>
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>

        <div className="map-legend">
          <div className="legend-item">
            <img
              src={pinkIcon.options.iconUrl}
              alt="Selected Venue"
              className="legend-icon"
            />{' '}
            Selected Venue
          </div>
          <div className="legend-item">
            <img
              src={blueIcon.options.iconUrl}
              alt="Matching Venue"
              className="legend-icon"
            />{' '}
            Matching Venue
          </div>
        </div>
      </div>

      <div className="selected-venues-bucket">
        <h3>Selected Venues</h3>
        {selectedVenues.length === 0 ? (
          <p>No venues selected.</p>
        ) : (
          <div className="selected-venues-list">
            {selectedVenues.map(name => {
              const details = venueDetailsMapping[name];
              return (
                <div
                  key={name}
                  className="selected-venue-item"
                >
                  <img
                    src={details.imageUrl}
                    alt={details.title}
                    className="selected-venue-image"
                  />
                  <p>{details.title}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <img
              src={DangerIcon}
              alt="Danger Icon"
              className="danger-icon"
            />
            <p>{modalMessage}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Distance;